/* eslint-disable max-lines */
import { useEffect } from 'react'
import { proxy, ref, useSnapshot } from 'valtio'
import { proxyMap } from 'valtio/utils'
import { intl } from 'lang/intl'

import { setLocalName } from '../Chat/blocs/useLocalName'
import { getWebinarConnectInfo } from '../global/blocs/useConnectInfo'
import {
  registerFatalDisconnect,
  setFatalError,
} from '../FatalError/FatalError'
import { DeferredSystem } from '../global/utils/DeferredSystem'
import { logMessage } from '../Logging/log'
import {
  destroy as devicesDestroy,
  init as devicesInit,
} from '../Devices/useDevices'
import { getIsClientSecondCam } from '../global/blocs/roomInfo'
import { useSpeakersQuery } from '../sharedLegacy/hooks/useSpeakersQuery'
import { floorState } from '../firebase/hooks/useFloor'
import { type Identifier } from '../firebase/model/floor-model'
import { getSettings } from '../Workspace/useSettings'
import { panelState } from '../firebase/hooks/usePanel'
import { showNotice } from '../Layout/Notices/Notices'
import { UserGesturePromise } from '../global/utils/UserGesturePromise'

import { ensureBrowserSupport } from './blocs/browserSupport'
import {
  destroy as devicesModuleDestroy,
  init as devicesModuleInit,
} from './blocs/deviceSelection'
import {
  destroy as audioMutingModuleDestroy,
  init as audioMutingModuleInit,
} from './blocs/audioMuting'
import {
  destroy as heartbeatsDestroy,
  init as heartbeatsInit,
  type ProviderInfo,
  type WebRTCConnectionStats,
} from './blocs/heartbeat'
import {
  destroy as videoPriorityModuleDestroy,
  init as videoPriorityModuleInit,
  syncRemoteTracksQuality,
} from './blocs/videoPriority'
import { init as reconnectionInit } from './blocs/reconnection'
import { init as floorValidityCheck } from './blocs/floorValidity'
import { notifyTrackAdded, notifyTrackRemoved } from './observer/trackObserver'

// ====== Store ====== //

type Track = {
  mediaStream: MediaStreamTrack
  isEnabled: boolean
}

type QualityStats = 'excellent' | 'acceptable' | 'bad'

type Participant = {
  id: number
  name: string
  role: 'presenter' | 'producer' | 'headless'
  isLocal: boolean
  joinedAt: number
  tracks: {
    camera: Track | null
    cameraAudio: Track | null
    screen: Track | null
    screenAudio: Track | null
    mobile: Track | null
  }
}

type Room = {
  status: 'booting' | 'connected' | 'disconnected' | 'connecting' | 'aborted'
  participants: Participant[]
  qualityStats: Map<Participant['id'], QualityStats>
}

type TrackPriority = 'low' | 'standard' | 'high'

type TrackKey = keyof Participant['tracks']
type LocalTrackKey = Exclude<TrackKey, 'mobile'>
type VideoTrackKey = Extract<TrackKey, 'camera' | 'screen' | 'mobile'>

type Provider = {
  publishTrack: (mediaTrack: MediaStreamTrack, key: TrackKey) => Promise<void>
  unpublishTrack: (mediaTrack: MediaStreamTrack, key: TrackKey) => void
  setTrackState: (key: LocalTrackKey, enabled: boolean) => void | Promise<void>
  setVideoTrackQuality: (
    remoteParticipantId: number,
    trackKey: VideoTrackKey,
    priority: TrackPriority
  ) => void
  getProviderInfo: () => ProviderInfo | null
  getConnectionStats: () => Promise<WebRTCConnectionStats | null>
}

const provider = new DeferredSystem<Provider>()
declare const window: Window & {
  TTWebRTC: { provider: { name?: string; instance?: Provider } }
}
window.TTWebRTC = { provider: {} }

const roomState = proxy<Room>({
  status: 'booting',
  participants: [],
  qualityStats: proxyMap(),
})

/** Makes sure the media stream is not proxied as it can lead to problems.
 * All tracks should go through this before being added to a valtio store */
const refTrack = (t: Track) => {
  return {
    mediaStream: ref(t.mediaStream),
    isEnabled: t.isEnabled,
  }
}

const connect = async () => {
  const webinarInfo = await getWebinarConnectInfo()
  if (webinarInfo.streaming_method === 'hardware') return

  if (['connected', 'aborted', 'connecting'].includes(roomState.status)) return

  if (!ensureBrowserSupport()) return

  const providerName = webinarInfo.webrtc_provider

  void devicesInit()

  let providerInstance: Provider | undefined
  roomState.participants = []

  switch (providerName) {
    case 'livekittwentythree':
    case 'livekit': {
      const livekit = await import('./providers/livekit/livekit')

      try {
        providerInstance = await livekit.connect(
          webinarInfo.webrtc_authenticator,
          webinarInfo.webrtc_room_url,
          providerName
        )
        registerFatalDisconnect(() => {
          livekit.disconnect({ abort: true })
          void stopAllTracks()
        })
      } catch (e) {
        livekit.disconnect({ abort: true })
        setFatalError('WEBRTC_PROVIDER_CONNECTION_FAILED', {
          reload: 'ask',
          reason: e,
        })
      }
      break
    }
    case 'twilio':
    default: {
      const twilio = await import('./providers/twilio/twilio')

      try {
        providerInstance = await twilio.connect(
          webinarInfo.webrtc_authenticator
        )
        registerFatalDisconnect(() => {
          twilio.disconnect({ abort: true })
          void stopAllTracks()
        })
      } catch (e) {
        twilio.disconnect({ abort: true })
        setFatalError('WEBRTC_PROVIDER_CONNECTION_FAILED', {
          reload: 'ask',
          reason: e,
        })
      }
    }
  }

  logMessage(`Connected to ${providerName} successfully`, { level: 'log' })

  if (providerInstance) {
    void provider.init(providerInstance)
    initModules()
    reconnectionInit()
    floorValidityCheck()

    window.TTWebRTC.provider = {
      name: providerName,
      instance: providerInstance,
    }
  }

  void updateLocalSpeakerName()
}

const initModules = () => {
  devicesModuleInit()
  audioMutingModuleInit()
  videoPriorityModuleInit()
  heartbeatsInit()
}

const destroyModules = () => {
  devicesModuleDestroy()
  audioMutingModuleDestroy()
  videoPriorityModuleDestroy()
  heartbeatsDestroy()
}

const disconnect = ({ abort = false }: { abort?: boolean } = {}) => {
  const newStatus = abort ? 'aborted' : 'disconnected'

  logMessage(`disconnected from webRTC provider`, {
    level: 'log',
    extra: { newStatus },
  })

  updateStatus(newStatus)
  void stopAllTracks()
  devicesDestroy()
  destroyModules()
}

// ====== Operations ====== //

const getConnectionStats = async () => {
  return (await provider.get()).getConnectionStats()
}

const updateStatus = (state: Room['status']) => {
  if (roomState.status === 'aborted') return
  roomState.status = state
}

const addParticipant = (participant: Participant) => {
  const existingParticipant = roomState.participants.find(
    (p) => p.id === participant.id
  )
  if (existingParticipant) {
    existingParticipant.name = participant.name
    existingParticipant.isLocal ||= participant.isLocal
    existingParticipant.role =
      participant.role !== 'presenter'
        ? participant.role
        : existingParticipant.role
    existingParticipant.joinedAt = Math.min(
      existingParticipant.joinedAt,
      participant.joinedAt
    )
    Object.entries(participant.tracks).forEach(([kind, track]) => {
      if (track) {
        logMessage('Adding track to existing participant', {
          level: 'debug',
          extra: {
            kind,
            participantId: participant.id,
          },
        })

        notifyTrackAdded(track.mediaStream)
        existingParticipant.tracks[kind as keyof Participant['tracks']] =
          refTrack(track)
      }
    })
  } else {
    roomState.participants.push({
      ...participant,
      tracks: {
        camera: participant.tracks.camera
          ? refTrack(participant.tracks.camera)
          : null,
        cameraAudio: participant.tracks.cameraAudio
          ? refTrack(participant.tracks.cameraAudio)
          : null,
        screen: participant.tracks.screen
          ? refTrack(participant.tracks.screen)
          : null,
        screenAudio: participant.tracks.screenAudio
          ? refTrack(participant.tracks.screenAudio)
          : null,
        mobile: participant.tracks.mobile
          ? refTrack(participant.tracks.mobile)
          : null,
      },
    })
  }

  if (participant.tracks.cameraAudio) {
    notifyTrackAdded(participant.tracks.cameraAudio.mediaStream)
  }
  if (participant.tracks.screenAudio) {
    notifyTrackAdded(participant.tracks.screenAudio.mediaStream)
  }

  roomState.participants.sort((a, b) => {
    if (a.isLocal) return -1
    if (b.isLocal) return 1
    return a.joinedAt - b.joinedAt
  })
}

const addTrack = (
  participantId: Participant['id'],
  key: TrackKey,
  track: Track
) => {
  const participant = roomState.participants.find((p) => p.id === participantId)

  if (!participant) {
    logMessage('Attempt to add track to nonexistent speaker', {
      level: 'error',
      extra: { key, participantId },
    })
    return undefined
  }

  if (participant.tracks[key]) {
    logMessage('Replacing existing track', {
      level: 'debug',
      extra: { key, participantId },
    })
  }

  participant.tracks[key] = refTrack(track)
  notifyTrackAdded(track.mediaStream)

  return participant.tracks[key]
}

const removeTrack = (
  participantId: Participant['id'],
  key: keyof Participant['tracks'],
  publicationId?: string
) => {
  const participant = roomState.participants.find((p) => p.id === participantId)
  if (!participant) {
    logMessage('Attempt to remove track from nonexistent speaker', {
      level: 'error',
      extra: { key, participantId },
    })
    return
  }

  const existingTrack = participant.tracks[key]

  if (!existingTrack) {
    logMessage('Attempt to remove nonexistent track', {
      level: 'debug',
      extra: { key, participantId },
    })
    return
  }

  if (
    publicationId &&
    existingTrack.mediaStream.id &&
    existingTrack.mediaStream.id !== publicationId
  ) {
    logMessage('Ignoring removal of outdated track publication', {
      level: 'debug',
      extra: { key, participantId },
    })
    return
  }

  const stream = existingTrack.mediaStream
  notifyTrackRemoved(stream)
  participant.tracks[key] = null
}

const publishTrack = async (
  mediaTrack: MediaStreamTrack,
  key: keyof Participant['tracks']
) => {
  await unpublishTrack(key)
  void setTrackContentHint(mediaTrack, key)
  await (await provider.get()).publishTrack(mediaTrack, key)
  syncRemoteTracksQuality()
}

const unpublishTrack = async (key: keyof Participant['tracks']) => {
  const localParticipant = getParticipants((p) => p.isLocal).at(0)
  if (localParticipant && localParticipant.tracks[key]) {
    const { mediaStream } = localParticipant.tracks[key]
    mediaStream.stop()
    ;(await provider.get()).unpublishTrack(mediaStream, key)
  }
}

const unpublishAll = async () => {
  const localParticipant = roomState.participants.find((p) => p.isLocal)
  if (localParticipant) {
    if (getIsClientSecondCam()) {
      void unpublishTrack('mobile')
    } else {
      void unpublishTrack('camera')
      void unpublishTrack('cameraAudio')
      void unpublishTrack('screen')
      void unpublishTrack('screenAudio')
    }
  }
}

const stopAllTracks = async () => {
  const localParticipant = roomState.participants.find((p) => p.isLocal)
  if (localParticipant) {
    if (getIsClientSecondCam()) {
      localParticipant.tracks.mobile?.mediaStream.stop()
    } else {
      localParticipant.tracks.camera?.mediaStream.stop()
      localParticipant.tracks.cameraAudio?.mediaStream.stop()
      localParticipant.tracks.screen?.mediaStream.stop()
      localParticipant.tracks.screenAudio?.mediaStream.stop()
    }
  }
}

const setTrackState = async (
  key: Exclude<keyof Participant['tracks'], 'mobile'>,
  enabled: boolean
) => {
  void (await provider.get()).setTrackState(key, enabled)
}

const setVideoTrackQuality = async (
  remoteParticipantId: number,
  key: VideoTrackKey,
  priority: TrackPriority
) => {
  ;(await provider.get()).setVideoTrackQuality(
    remoteParticipantId,
    key,
    priority
  )
}

const setTrackContentHint = async (
  mediaStreamTrack: MediaStreamTrack,
  key: keyof Participant['tracks']
) => {
  const features = await getSettings()
  let contentHint = ''
  switch (key) {
    case 'camera':
    case 'mobile':
      break
    case 'screen':
      switch (features.webinars.screenShareContentHint) {
        case 'detail':
          contentHint = 'detail'
          break
        case 'text':
          contentHint = 'text'
          break
        case 'motion':
          contentHint = 'motion'
          break
        default:
          contentHint = ''
      }
      break
    case 'cameraAudio':
      contentHint = 'speech'
      break
    default:
      break
  }

  // Setting content hint is optional and may not be supported by the browser
  try {
    // eslint-disable-next-line no-param-reassign
    mediaStreamTrack.contentHint = contentHint
  } catch (e) {
    // nothing
  }
}

const updateTrackState = (track: Track, isEnabled: boolean) => {
  // eslint-disable-next-line no-param-reassign
  track.isEnabled = isEnabled
}

const updateParticipantQuality = (id: number, newQuality: QualityStats) => {
  roomState.qualityStats.set(id, newQuality)
}

const removeParticipant = (id: number) => {
  roomState.participants = roomState.participants.filter((p) => p.id !== id)
}

const getParticipants = (filter?: (p: Participant) => boolean) => {
  const { participants } = roomState
  if (!filter) return participants
  return participants.filter(filter)
}

const getHumanParticipants = () => {
  return getParticipants((p) => p.role !== 'headless')
}

const getProviderInfo = async () => (await provider.get()).getProviderInfo()

const getRoomStatus = () => roomState.status

const mapFloorIdentifierToTrackName = (
  identifier: Identifier | null | undefined
): keyof Participant['tracks'] | null => {
  if (!identifier) return null
  if (identifier === 'screenshare') return 'screen'
  if (identifier === 'mobile') return 'mobile'
  return 'camera'
}
const getSelectedTrackOnStage = (): string[] => {
  const { floor } = floorState
  const panel = panelState

  if (!floor) return []

  switch (floor.layout) {
    case 'panel': {
      if (panel.mode === 'auto') {
        return roomState.participants
          .filter((p) => p.role !== 'headless' && p.tracks.camera?.isEnabled)
          .map((p) => p.tracks.camera?.mediaStream.id)
          .filter((id) => id !== undefined)
      }

      return roomState.participants
        .filter(
          (p) =>
            panel.participantIds.includes(p.id) && p.tracks.camera?.isEnabled
        )
        .map((p) => p.tracks.camera?.mediaStream.id)
        .filter((id) => id !== undefined)
    }
    case 'tray': {
      return ['tray']
    }
    case 'solo': {
      const trackName = mapFloorIdentifierToTrackName(floor.identifier)
      if (!trackName) return []
      const participant = roomState.participants.find(
        (p) => p.id === floor.participantId && p.tracks[trackName]?.isEnabled
      )
      const trackSelected = participant?.tracks[trackName]?.mediaStream.id
      return trackSelected ? [trackSelected] : []
    }
    case 'side-by-side': {
      const trackName = mapFloorIdentifierToTrackName(floor.identifier)
      if (!trackName) return []

      const participant = roomState.participants.find(
        (p) => p.id === floor.participantId
      )

      if (!participant) return []
      const tracks = []
      if (participant.tracks[trackName]?.isEnabled) {
        tracks.push(participant.tracks[trackName].mediaStream.id)
      }
      if (participant.tracks.camera?.isEnabled) {
        tracks.push(participant.tracks.camera.mediaStream.id)
      }
      return tracks
    }
    default:
      return []
  }
}

// ====== React hooks ====== //

const useRoomStatus = () => useSnapshot(roomState).status

const useParticipants = (filter?: (p: Participant) => boolean) => {
  const { participants } = useSnapshot(roomState)
  if (!filter) return participants
  return participants.filter(filter)
}

const useHumanParticipants = () => {
  return useParticipants((p) => p.role !== 'headless')
}

const useRemoteParticipants = () => {
  return useParticipants((p) => !p.isLocal && p.role !== 'headless')
}

const useLocalParticipant = (): Participant | undefined => {
  return useParticipants((p) => p.isLocal)[0]
}

const useHeadlessParticipant = (): Participant | undefined => {
  return useParticipants((p) => p.role === 'headless')[0]
}

const useParticipant = (participantId: Participant['id']) => {
  const { participants } = useSnapshot(roomState)
  return participants.find((p) => p.id === participantId)
}

const useParticipantQualityStats = (participantId: Participant['id']) => {
  return useSnapshot(roomState.qualityStats).get(participantId)
}

type FullParticipantInfo = Participant & {
  name: string
  isShowingTvCards?: boolean
  role?: string
}

const useFullParticipantInfo = (
  participantId: Participant['id']
): FullParticipantInfo | undefined => {
  const participant = useParticipant(participantId)
  const { data: speakers = [] } = useSpeakersQuery()

  const speaker = speakers.find((s) => s.id === participantId)

  if (!participant) return undefined

  return {
    ...participant,
    name: speaker?.name ?? participant.name,
    isShowingTvCards: speaker?.isShowingTitleCard,
  }
}

const updateLocalSpeakerName = async () => {
  const participant = getParticipants((p) => p.isLocal).at(0)
  if (!participant) return

  setLocalName({
    speakerId: participant.id,
    full: participant.name,
    sender: participant.name,
  })
}

const userGesturePromise = new UserGesturePromise((resolve) => {
  showNotice({
    message: intl.formatMessage({
      defaultMessage:
        'Your browser may be blocking audio from other speakers. Click allow to retry playing audio.',
      id: 'Y+00Ns',
    }),
    cta: {
      dismissOnClick: true,
      label: intl.formatMessage({
        defaultMessage: 'Allow',
        id: 'y/bmsG',
      }),
      onClick: resolve,
    },
    isClosable: false,
  })
})

/* eslint-disable no-param-reassign */
const useTrack = (
  track: Track,
  element: HTMLVideoElement | HTMLAudioElement | null
) => {
  useEffect(() => {
    if (!element) return undefined

    if (!track.isEnabled && element instanceof HTMLVideoElement) {
      element.pause()
      element.srcObject = null
      return undefined
    }

    element.srcObject = new MediaStream([track.mediaStream])
    element.autoplay = true

    element.play().catch(async (error: unknown) => {
      await userGesturePromise
      try {
        await element.play()
      } catch (error2) {
        showNotice({
          message: intl.formatMessage({
            defaultMessage:
              'Could not play audio from other speakers. Visit our support center for instructions on how to fix this.',
            id: 'Rbs2xK',
          }),
          isClosable: true,
        })
        logMessage('Failed to play track', {
          level: 'warning',
          extra: {
            cause: {
              error1: error,
              error2,
            },
          },
        })
      }
    })

    return () => {
      element.pause()
      element.srcObject = null
    }
  }, [element, track.mediaStream, track.isEnabled])
}

// ====== Helpers ====== //

export {
  type Track,
  type Room,
  type Participant,
  type TrackKey,
  type LocalTrackKey,
  type VideoTrackKey,
  type TrackPriority,
  type QualityStats,
  roomState,
  connect,
  disconnect,
  updateStatus,
  addParticipant,
  addTrack,
  removeTrack,
  publishTrack,
  unpublishTrack,
  unpublishAll,
  removeParticipant,
  setTrackState,
  setVideoTrackQuality,
  updateParticipantQuality,
  getParticipants,
  getHumanParticipants,
  useRoomStatus,
  useParticipants,
  useHumanParticipants,
  useRemoteParticipants,
  useLocalParticipant,
  useHeadlessParticipant,
  useParticipant,
  useParticipantQualityStats,
  useFullParticipantInfo,
  useTrack,
  getProviderInfo,
  getRoomStatus,
  getConnectionStats,
  getSelectedTrackOnStage,
  updateTrackState,
}
